import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MarketingNav } from "../components/Nav";
import Footer from "../components/Footer";
import SectionTitle from "../components/SectionTitle";
import VibeTag from "../components/VibeTag";
import ProgressionIndicator from "../components/ProgressionIndicator";
import { prospects, testimonials } from "../data/mockData";
import { track } from "../lib/analytics";

let deferredPrompt = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
  });
}

function HeroMosaic() {
  const featured = prospects.slice(0, 5);
  return (
    <div className="relative h-[560px] md:h-[640px] w-full">
      <div className="absolute inset-0 grid grid-cols-6 grid-rows-6 gap-3">
        <div className="col-span-3 row-span-4 relative rounded-3xl overflow-hidden ring-gold animate-fade-up delay-200">
          <img src={featured[0].image} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#05070d] via-transparent" />
          <div className="absolute bottom-5 left-5 right-5">
            <div className="text-[10px] tracking-[0.24em] uppercase text-[#F2DE94] mb-1">Top Match</div>
            <div className="font-display text-2xl">{featured[0].name}</div>
          </div>
        </div>
        <div className="col-span-3 row-span-3 relative rounded-3xl overflow-hidden ring-1 ring-white/10 animate-fade-up delay-300">
          <img src={featured[1].image} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#05070d] via-transparent" />
          <div className="absolute bottom-4 left-4">
            <div className="font-display text-xl">{featured[1].name}</div>
          </div>
        </div>
        <div className="col-span-2 row-span-2 relative rounded-2xl overflow-hidden ring-1 ring-white/10 animate-fade-up delay-400">
          <img src={featured[2].image} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#05070d] via-transparent" />
          <div className="absolute bottom-3 left-3 text-sm font-display">{featured[2].name}</div>
        </div>
        <div className="col-span-2 row-span-2 relative rounded-2xl overflow-hidden ring-burgundy animate-fade-up delay-500">
          <img src={featured[3].image} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#05070d] via-transparent" />
          <div className="absolute bottom-3 left-3 text-sm font-display">{featured[3].name}</div>
        </div>
        <div className="col-span-3 row-span-2 relative rounded-2xl overflow-hidden ring-1 ring-white/10 animate-fade-up delay-600">
          <img src={featured[4].image} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#05070d] via-transparent" />
          <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
            <span className="font-display text-lg">{featured[4].name}</span>
            <span className="glass rounded-full px-2 py-0.5 text-[11px] font-mono">94%</span>
          </div>
        </div>
      </div>
      {/* corner labels */}
        <div className="absolute -top-3 -right-3 glass rounded-full px-3 py-1.5 text-[11px] tracking-[0.2em] uppercase text-white/75">
          7 → 1
        </div>
      </div>
    );
  }

function MobileAppBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const dismissed = sessionStorage.getItem("bm_app_banner_dismissed");

    if (isMobile && !dismissed) {
      track("install_prompt_viewed", { page: "/" });
      setVisible(true);
    }
  }, []);

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 inset-x-0 z-50 bg-[#080B14] border-t border-white/10 px-4 py-3 flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <div className="h-10 w-10 rounded-xl bg-gradient-gold shrink-0 grid place-items-center font-display text-[#080B14] font-bold text-sm">B</div>
        <div className="min-w-0">
          <div className="text-white text-sm font-semibold truncate">Bora Moyo</div>
          <div className="text-white/50 text-xs truncate">Add Bora Moyo to your home screen</div>
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <a
          href="/install"
          className="rounded-full bg-gradient-gold text-[#080B14] text-xs font-semibold px-4 py-1.5"
          onClick={async (e) => {
            e.preventDefault();
            track("install_prompt_clicked", { page: "/" });

            if (deferredPrompt) {
              deferredPrompt.prompt();
              await deferredPrompt.userChoice;
              deferredPrompt = null;
            } else {
              window.location.href = "/install";
            }
          }}
        >
          Install
        </a>

        <button
          onClick={() => {
            sessionStorage.setItem("bm_app_banner_dismissed", "1");
            setVisible(false);
          }}
          className="text-white/40 hover:text-white/70 text-lg leading-none px-1"
          aria-label="Dismiss"
        >
          ×
        </button>
      </div>
    </div>
  );
}

export default function Landing() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    track("home_view", { page: "/" });
  }, []);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="bg-gradient-hero min-h-screen text-white overflow-hidden">
      <MarketingNav />

      {/* HERO */}
      <section className="relative pt-36 pb-24 md:pt-52 md:pb-36 flex items-center justify-center">
          <div className="mx-auto max-w-6xl px-6 md:px-12 relative z-10 animate-fade-up grid lg:grid-cols-[0.85fr_1.15fr] gap-10 lg:gap-16 items-center">
            <div className="flex justify-center lg:justify-end">
              <div className="w-[240px] md:w-[330px] lg:w-[390px] rounded-[2rem] bg-[#0C111D] p-5 ring-1 ring-[#D4AF37]/25 shadow-[0_0_80px_rgba(212,175,55,0.14)]">
                <img src="/logo.png" alt="Bora Moyo" className="w-full h-auto object-contain" />
              </div>
            </div>

            <div className="text-center lg:text-left">
              <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-4">
                Dating with intention
              </div>
              <h1 className="font-display text-[40px] leading-[1.05] md:text-[58px] lg:text-[70px] tracking-tight">
                Choose seven. Follow the journey. Find the best heart.
              </h1>
              <p className="mt-6 text-white/68 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto lg:mx-0">
                A guided compatibility journey designed for real connection — not endless swiping.
              </p>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 text-left max-w-2xl mx-auto lg:mx-0">
                {[
                  ["01", "Choose your seven"],
                  ["02", "Compare through guided rounds"],
                  ["03", "Move toward one aligned match"],
                ].map(([n, text]) => (
                  <div key={n} className="glass rounded-2xl px-4 py-3">
                    <div className="text-[10px] tracking-[0.22em] uppercase text-[#D4AF37]">{n}</div>
                    <div className="text-sm text-white/75 mt-1">{text}</div>
                  </div>
                ))}
              </div>

              <div className="mt-10 flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-4">
                <Link
                  to="/onboarding"
                  data-testid="hero-cta-primary"
                  onClick={() => track("cta_start_clicked", { page: "/" })}
                  className="inline-flex items-center gap-2 rounded-full bg-gradient-gold text-[#080B14] font-semibold px-8 py-4 glow-gold hover:scale-[1.02] transition-transform"
                >
                  Start Your Journey
                </Link>
                <Link
                  to="/guide"
                  onClick={() => track("learn_more_clicked", { page: "/" })}
                  className="text-white/60 hover:text-white/90 transition-colors text-sm tracking-wide py-4"
                >
                  See how it works
                </Link>
              </div>
            </div>
          </div>
      </section>

      {/* MOSAIC — below-fold visual teaser */}
      <section className="pb-16">
        <div className="mx-auto max-w-5xl px-6 md:px-12">
          <HeroMosaic />
        </div>
      </section>

      {/* 7 → 1 PROGRESSION STRIP */}
      <section className="relative pb-4" data-testid="progression-strip">
        <div className="mx-auto max-w-5xl px-6 md:px-12">
          <ProgressionIndicator variant="strong" current={2} />
        </div>
      </section>

      {/* WHY DIFFERENT — ABOVE THE FOLD */}
      <section className="py-16 md:py-20 border-t border-white/5">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="text-center mb-10">
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-3">
              Built differently
            </div>
            <h2 className="font-display text-3xl md:text-4xl leading-tight">
              Why BoraMoyo feels different
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              {
                n: "01",
                title: "More structure",
                body: "Guided profiles and a structured matching process — not a random feed of faces.",
              },
              {
                n: "02",
                title: "Better signal",
                body: "Go beyond photos and instant swipes. Rounds reveal values, voice, chemistry, and real effort.",
              },
              {
                n: "03",
                title: "Stronger outcomes",
                body: "Designed for meaningful connections — not engagement metrics or endless scrolling.",
              },
            ].map((item) => (
              <div
                key={item.n}
                className="glass rounded-3xl p-7 hover:border-[rgba(212,175,55,0.3)] hover:-translate-y-1 transition-all"
              >
                <div className="font-mono text-xs text-[#D4AF37] tracking-[0.2em] mb-4">{item.n}</div>
                <div className="font-display text-2xl mb-3 leading-tight">{item.title}</div>
                <p className="text-sm text-white/55 leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* JOURNEY STEPS */}
      <section id="how" className="py-24 md:py-32 relative scroll-mt-24">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <SectionTitle
            eyebrow="How it works · four movements"
            title="Seven prospects. Five rounds. One Bora Moyo."
            subtitle="A structured, cinematic decision — not an infinite feed."
          />

          <div className="mt-14 grid md:grid-cols-4 gap-5">
            {[
              {
                n: "01",
                t: "Choose seven",
                d: "Hand-pick up to seven prospects from a refined match pool.",
              },
              {
                n: "02",
                t: "Compare through rounds",
                d: "Values, voice, chemistry, effort — each round reveals something new.",
              },
              {
                n: "03",
                t: "Narrow with intention",
                d: "Side-by-side comparisons with weighted compatibility and green flags.",
              },
              {
                n: "04",
                t: "One remains",
                d: "Two finalists become your Bora Moyo — the one with the best heart.",
              },
            ].map((s) => (
              <div
                key={s.n}
                className="glass rounded-3xl p-6 md:p-7 relative overflow-hidden hover:border-[rgba(212,175,55,0.3)] hover:-translate-y-1 transition-all"
              >
                <div className="font-mono text-xs text-[#D4AF37] tracking-[0.2em]">{s.n}</div>
                <div className="font-display text-2xl mt-4 leading-tight">{s.t}</div>
                <div className="text-sm text-white/55 mt-3 leading-relaxed">{s.d}</div>
                <div className="mt-6 h-12 flex items-end">
                  <div className="h-[3px] w-12 bg-gradient-gold rounded-full" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SIGNATURE: THE SEVEN */}
      <section id="journey" className="py-24 md:py-32 relative scroll-mt-24">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="grid md:grid-cols-[1fr_1.1fr] gap-14 items-center">
            <div>
              <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-3">
                Signature screen
              </div>
              <h2 className="font-display text-4xl md:text-6xl leading-[1.02]">
                A shortlist,
                <br />
                <span className="italic text-gradient-gold">not a feed.</span>
              </h2>
              <p className="mt-5 text-white/65 text-lg leading-relaxed max-w-lg">
                Your draft of seven is treated like a private collection. You
                move through rounds with intention — nothing discarded in haste.
              </p>
              <div className="mt-8 flex flex-wrap gap-2">
                {["Values", "Communication", "Chemistry", "Future Goals", "Effort", "Finalist"].map(
                  (t, i) => (
                    <VibeTag key={t} tone={i === 2 ? "gold" : "default"}>
                      Round 0{i + 1} · {t}
                    </VibeTag>
                  )
                )}
              </div>
            </div>

            <div className="relative">
              <div className="glass-strong rounded-3xl p-6 md:p-8">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <div className="font-display text-2xl">Your Seven</div>
                    <div className="text-xs text-white/55">5 advanced · 2 path closed</div>
                  </div>
                  <div className="glass rounded-full px-3 py-1.5 text-xs font-mono">R3 · Chemistry</div>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  {prospects.slice(0, 7).map((p, i) => (
                    <div
                      key={p.id}
                      className={`relative rounded-xl overflow-hidden aspect-[3/4] ${
                        i < 5 ? "ring-forest" : "opacity-45 ring-1 ring-white/10"
                      }`}
                    >
                      <img src={p.image} alt={p.name} className="absolute inset-0 h-full w-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#05070d]/90 via-transparent" />
                      <div className="absolute bottom-2 inset-x-2 text-[11px] font-display">{p.name}</div>
                      {i < 5 && (
                        <div className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-[#4AA36A]" />
                      )}
                    </div>
                  ))}
                  <div className="relative rounded-xl border border-dashed border-white/10 grid place-items-center text-white/40 text-[10px] uppercase tracking-[0.2em] aspect-[3/4]">
                    Seat 7
                  </div>
                </div>
                <div className="hairline my-6" />
                <div className="grid grid-cols-3 gap-3 text-center">
                  {[
                    { k: "Strongest", v: "Pending", t: "--" },
                    { k: "Highest effort", v: "Pending", t: "--" },
                    { k: "Surprise", v: "Kai", t: "+12" },
                  ].map((x) => (
                    <div key={x.k} className="rounded-xl bg-white/[0.03] p-3">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">{x.k}</div>
                      <div className="font-display mt-1">{x.v}</div>
                      <div className="text-xs text-[#F2DE94]">{x.t}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURE BENTO */}
      <section className="py-24 md:py-32 border-t border-white/5">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <SectionTitle
            eyebrow="What makes it different"
            title="Designed for decisions — not distractions."
          />
          <div className="mt-14 grid md:grid-cols-3 gap-5">
            {[
              {
                t: "Weighted compatibility",
                d: "Tune what matters most. The app listens.",
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5" className="h-6 w-6 stroke-[#D4AF37]">
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 3v18M3 12h18" opacity=".4" />
                    <circle cx="12" cy="12" r="4" />
                  </svg>
                ),
              },
              {
                t: "Rounds, not swipes",
                d: "Values, chemistry, communication — structured and cinematic.",
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5" className="h-6 w-6 stroke-[#D4AF37]">
                    <path d="M4 5h4v14H4M20 5h-4v14h4M8 12h8" />
                  </svg>
                ),
              },
              {
                t: "Intentional video",
                d: "20-second prompts. No infinite scroll. No noise.",
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5" className="h-6 w-6 stroke-[#D4AF37]">
                    <rect x="3" y="6" width="14" height="12" rx="2" />
                    <path d="m17 10 4-2v8l-4-2z" fill="currentColor" />
                  </svg>
                ),
              },
              {
                t: "Green flags, gently noted",
                d: "We highlight consistency, effort, and kindness. Not red letters.",
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5" className="h-6 w-6 stroke-[#4AA36A]">
                    <path d="M4 20V5a1 1 0 0 1 1-1h11l-2 3 2 3H5" />
                  </svg>
                ),
              },
              {
                t: "Top match reveal",
                d: "A quiet, cinematic moment for the connection you've earned.",
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5" className="h-6 w-6 stroke-[#D4AF37]">
                    <path d="m12 3 2.5 6 6.5 1-4.8 4.6 1.1 6.4L12 18.5 6.7 21l1.1-6.4L3 10l6.5-1Z" />
                  </svg>
                ),
              },
              {
                t: "Private by default",
                d: "Your shortlist is yours. No leaderboards. No public elimination.",
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5" className="h-6 w-6 stroke-[#D4AF37]">
                    <rect x="4" y="10" width="16" height="10" rx="2" />
                    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
                  </svg>
                ),
              },
            ].map((f, i) => (
              <div
                key={f.t}
                className={`glass rounded-3xl p-7 relative overflow-hidden hover:-translate-y-1 transition-all ${
                  i === 0 ? "md:col-span-1" : ""
                }`}
              >
                <div className="h-11 w-11 rounded-xl grid place-items-center bg-[rgba(212,175,55,0.1)] ring-1 ring-[rgba(212,175,55,0.25)] mb-5">
                  {f.icon}
                </div>
                <div className="font-display text-2xl leading-tight">{f.t}</div>
                <div className="text-sm text-white/55 mt-2 leading-relaxed">{f.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY THIS IS DIFFERENT */}
      <section className="pt-20 md:pt-24 pb-10 border-t border-white/5" data-testid="why-different">
        <div className="mx-auto max-w-6xl px-6 md:px-12 grid md:grid-cols-[1fr_1.15fr] gap-14 items-start">
          <div className="md:sticky md:top-28">
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-4">
              Why this is different
            </div>
            <h2 className="font-display text-4xl md:text-5xl leading-[1.05]">
              Not another <br />
              dating <span className="italic text-gradient-gold">feed.</span>
            </h2>
            <p className="mt-5 text-white/60 max-w-md leading-relaxed">
              Bora Moyo is designed for one thing: helping you arrive, calmly,
              at the person with the <span className="text-[#F2DE94]">best heart</span>.
            </p>
          </div>
          <div className="grid gap-3">
            {[
              { k: "01", t: "No endless swiping", d: "Seven people. A fixed, finite shortlist." },
              { k: "02", t: "Structured decision-making", d: "Each round narrows with intention — not impulse." },
              { k: "03", t: "Guided compatibility rounds", d: "Values, voice, chemistry, effort. All weighted, all transparent." },
              { k: "04", t: "One clear outcome", d: "You meet your top match. Not a list. Not a queue. Just one." },
            ].map((r) => (
              <div
                key={r.k}
                className="glass rounded-2xl p-5 md:p-6 grid grid-cols-[auto_1fr] gap-5 items-start hover:border-[rgba(212,175,55,0.3)] transition-colors"
              >
                <div className="font-mono text-[#D4AF37] text-sm tracking-[0.18em] pt-1">{r.k}</div>
                <div>
                  <div className="font-display text-xl">{r.t}</div>
                  <div className="text-sm text-white/60 mt-1">{r.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EMOTIONAL HOOK */}
      <section className="py-28 md:py-40 relative" data-testid="emotional-hook">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[420px] w-[820px] rounded-full bg-[#D4AF37] opacity-[0.05] blur-[140px]" />
        </div>
        <div className="mx-auto max-w-5xl px-6 md:px-12 relative text-center">
          <div className="text-[11px] tracking-[0.34em] uppercase text-[#D4AF37]/80 mb-8">
            A defining thought
          </div>
          <div className="font-display text-[44px] md:text-[72px] lg:text-[88px] leading-[1.04] tracking-tight text-white/90">
            Most people never <span className="italic text-white/40">choose</span>.
            <br />
            They just keep <span className="italic text-white/40">looking</span>.
          </div>
          <div className="hairline w-40 mx-auto my-12" />
          <div className="font-display italic text-3xl md:text-5xl text-gradient-gold">
            Bora Moyo helps you decide.
          </div>
          <div className="mt-8 text-xs md:text-sm tracking-[0.22em] uppercase text-white/45">
            Only one remains.
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 md:py-32 border-t border-white/5">
        <div className="mx-auto max-w-6xl px-6 md:px-12">
          <SectionTitle eyebrow="Quiet praise" title="Told to us, quietly." />
          <div className="mt-14 grid md:grid-cols-3 gap-5">
            {testimonials.map((t, i) => (
              <figure
                key={t.name}
                className="glass rounded-3xl p-7 leading-relaxed"
              >
                <div className="text-[#D4AF37] text-4xl font-display leading-none">"</div>
                <blockquote className="font-display text-xl md:text-2xl mt-2 leading-snug">
                  {t.quote}
                </blockquote>
                <figcaption className="mt-6 text-sm text-white/55 tracking-wider">
                  — {t.name}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* DIFFERENTIATION STATEMENT */}
      <section className="py-24 md:py-32 border-t border-white/5">
        <div className="mx-auto max-w-6xl px-6 md:px-12">
          <div className="grid md:grid-cols-[1fr_1.1fr] gap-14 items-center">
            <div>
              <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-5">
                Built differently
              </div>
              <h2 className="font-display text-4xl md:text-5xl leading-[1.04]">
                Most swipe apps<br />
                are random.<br />
                <span className="italic text-gradient-gold">BoraMoyo isn't.</span>
              </h2>
              <p className="mt-6 text-white/60 text-base md:text-lg leading-relaxed max-w-lg">
                Most platforms are built for endless swiping. BoraMoyo introduces
                structure — so decisions feel more intentional and matches lead to
                more meaningful connections.
              </p>
            </div>
            <div className="grid gap-4">
              {[
                {
                  n: "01",
                  title: "Guided selection",
                  body: "Not endless profiles. A curated shortlist of seven — chosen by you, with intention.",
                },
                {
                  n: "02",
                  title: "Higher signal",
                  body: "Beyond surface-level attraction. Rounds reveal values, voice, chemistry, and real effort.",
                },
                {
                  n: "03",
                  title: "Better outcomes",
                  body: "Built for real connection — not engagement metrics, activity streaks, or endless scrolling.",
                },
              ].map((item) => (
                <div
                  key={item.n}
                  className="glass rounded-2xl p-5 md:p-6 grid grid-cols-[auto_1fr] gap-5 items-start hover:border-[rgba(212,175,55,0.3)] transition-colors"
                >
                  <div className="font-mono text-[#D4AF37] text-sm tracking-[0.18em] pt-0.5">{item.n}</div>
                  <div>
                    <div className="font-display text-xl leading-tight">{item.title}</div>
                    <p className="text-sm text-white/55 mt-1.5 leading-relaxed">{item.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="py-24 md:py-32 border-t border-white/5">
        <div className="mx-auto max-w-5xl px-6 md:px-12 text-center">
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-4">
            An invitation
          </div>
          <h2 className="font-display text-4xl md:text-6xl leading-[1.04]">
            Seven people. Five rounds.
            <br />
            <span className="italic text-gradient-gold">One Bora Moyo.</span>
          </h2>
          <Link
            to="/onboarding"
            data-testid="final-cta"
            className="mt-10 inline-flex items-center gap-2 rounded-full bg-gradient-gold text-[#080B14] font-semibold px-8 py-4 glow-gold hover:scale-[1.02] transition-transform"
          >
            Find your Bora Moyo
            <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current"><path d="M5 12h14m-5-5 5 5-5 5"/></svg>
          </Link>
          <div className="text-xs text-white/40 mt-5">
            Takes 4 minutes · Free to start
          </div>
        </div>
      </section>

      <Footer />
      <MobileAppBanner />
    </div>
  );
}
